import { aabnTilfoejMaskine, setAddCardCollapsed, showPage } from './kerne.js';
import { hasFMC130, isBatteryMachine, loadBatteryStatus, loadBatteryStatusFull, loadFMC130Data } from './batteri.js';
import { machines, sess, setMachines } from './state.js';
import { MFULL } from '../lib/datoer.js';
import { decodeHtml, esc, escJs, visTekst } from '../lib/tekst.js';
import { authFetch, closeModal, toast } from './ui.js';
import { sb } from '../lib/supabase.js';

/**
 * De felter på en maskine, kunden må se.
 *
 * Bevidst liste frem for '*'. To grunde:
 *
 *  1. admin_note er servicechefens interne note. Den skal ikke ud i
 *     browseren, og databasen naegter den nu ogsaa — se db/adgang.sql.
 *  2. Netop derfor VIRKER '*' ikke laengere: PostgREST oversaetter det
 *     til SELECT *, som rammer en kolonne rollen ikke maa laese, og hele
 *     kaldet fejler med "permission denied". Det gaelder ogsaa .select()
 *     uden argumenter efter et insert.
 *
 * Tilfoejes en kolonne til machines, som kunden skal se, skal den med
 * her OG i GRANT SELECT i db/adgang.sql.
 */
export const MASKINE_FELTER =
  'id,user_id,sku,name,service_plan,plan_expiry,notes,added_date,' +
  'custom_brand,custom_model,custom_serial,custom_image_url,' +
  'machine_type,battery_imei';

export async function loadMachines() {
  const spin = document.getElementById('mcSpin');
  if (spin) spin.style.display='block';

  // Ét samlet kald for maskiner
  const { data, error } = await sb.from('machines')
    .select(MASKINE_FELTER)
    .order('added_date', { ascending: false });
  if (spin) spin.style.display='none';
  if (error) { toast('❌ Kunne ikke hente maskiner'); return; }
  setMachines(data || []);
  if (!machines.length) { renderMachines(); return; }

  // Ét samlet kald for produktmeta (kun DK-TEC maskiner)
  const skus = machines.map(m => m.sku).filter(Boolean);
  if (skus.length) {
    const { data: prods } = await sb.from('products').select('sku,name,url,image_url').in('sku', skus);
    if (prods) {
      const prodMap = Object.fromEntries(prods.map(p => [p.sku, p]));
      machines.forEach(m => { m._prodMeta = m.sku ? (prodMap[m.sku] || {}) : {}; });
    }
  } else {
    machines.forEach(m => { m._prodMeta = {}; });
  }

  // Ét samlet kald for al service_log — ingen per-maskine kald
  const mcIds = machines.map(m => m.id);
  const { data: svcAll } = await sb.from('service_log').select('*').in('machine_id', mcIds).order('service_date', {ascending:false});
  if (svcAll) {
    const svcMap = {};
    svcAll.forEach(s => {
      if (!svcMap[s.machine_id]) svcMap[s.machine_id] = [];
      svcMap[s.machine_id].push(s);
    });
    machines.forEach(m => { m._svcLog = svcMap[m.id] || []; m._lastSvc = m._svcLog[0]?.service_date || null; });
  } else {
    machines.forEach(m => { m._svcLog = []; m._lastSvc = null; });
  }

  // Sporingsenheder hentes gennem en serverfunktion.
  //
  // Her stod før et direkte opslag i tracker_devices med browserens
  // nøgle. RLS på den tabel giver kun administratorer adgang, så
  // opslaget gav altid nul rækker — uden fejl. GPS-visningen dukkede
  // derfor aldrig op, og intet sted sagde hvorfor.
  if (sess) {
    try {
      const res = await authFetch('/.netlify/functions/mine-trackere');
      if (res.ok) {
        const { enheder } = await res.json();
        // Kobl først på maskine-id, som er den præcise binding. SKU er
        // faldbakken for enheder, der er tilknyttet før koblingen fandtes.
        const prMaskine = new Map();
        const prSku = new Map();
        (enheder || []).forEach(e => {
          if (e.machine_id) prMaskine.set(e.machine_id, e);
          else if (e.sku)   prSku.set(e.sku, e);
        });
        machines.forEach(mc => {
          const enhed = prMaskine.get(mc.id) || (mc.sku ? prSku.get(mc.sku) : null);
          if (enhed) mc._tracker = enhed;
        });
      }
    } catch (e) {
      console.error('Kunne ikke hente sporingsenheder:', e);
    }
  }

  renderMachines();
}

export function toggleKontakt() {
  const panel = document.getElementById('kontaktPanel');
  const btn = document.getElementById('kontaktBtn');
  const open = panel.style.display === 'none';
  panel.style.display = open ? 'block' : 'none';
  btn.textContent = open ? '✕ Luk' : '📞 Kontakt';
  btn.style.background = open ? 'rgba(255,255,255,.1)' : 'rgba(99,158,127,.2)';
}

export function renderMachines() {
  const list = document.getElementById('mcList'); list.innerHTML = '';
  const countEl = document.getElementById('mcCount');
  if (countEl) countEl.textContent = machines.length ? `(${machines.length})` : '';
  if (!machines.length) {
    list.innerHTML='<div class="empty"><div class="ei">🔧</div><p>Du har ingen maskiner endnu.<br>Tilføj din første maskine nedenfor.</p></div>';
    aabnTilfoejMaskine(); // Ny bruger — vis panelet med det samme
    return;
  }
  setAddCardCollapsed(true); // Har maskiner — fold ind
  machines.forEach(m => {
    try {
      renderCard(m);
    } catch(e) {
      console.error('renderCard fejl:', e, m);
      // Vis et minimalt nødkort så maskinen ikke forsvinder
      const list = document.getElementById('mcList');
      const fallback = document.createElement('div');
      fallback.className = 'mc';
      fallback.innerHTML = '<div class="mch"><div class="mcinfo"><div class="mcname">' + visTekst(m.name) + '</div><div class="mcsku">' + esc(m.sku || '') + '</div></div></div>';
      list.appendChild(fallback);
    }
  });
  tilfoejFelt();
}

/**
 * "Tilføj maskine" som sidste felt i gitteret.
 *
 * Knappen lå før nederst på siden, under alle genvejene — langt fra den
 * liste den hører til. Som felt i gitteret gør den to ting på én gang:
 * viser hvor maskinlisten slutter, og ligger hvor man leder efter den.
 */
function tilfoejFelt() {
  const list = document.getElementById('mcList');
  const felt = document.createElement('button');
  felt.type = 'button';
  felt.className = 'mc-add';
  felt.innerHTML =
    '<span class="mc-add-plus" aria-hidden="true">+</span>' +
    '<strong>Tilføj maskine</strong>' +
    '<span>Scan QR-koden på maskinen, eller søg på varenummeret</span>';
  felt.addEventListener('click', aabnTilfoejMaskine);
  list.appendChild(felt);
}

/**
 * Omdøbning af en maskine.
 *
 * Nødvendigt nu hvor to ens maskiner kan oprettes: uden omdøbning ville
 * de hedde det samme og være umulige at skelne. Nyttigt i øvrigt for
 * alle — "Minilæsser ved hallen" siger mere end produktnavnet.
 */

/** Maskinen der omdøbes lige nu. Nulstilles når dialogen lukkes. */
let omdoeberMaskineId = null;

export function omdoebMaskine(id) {
  const maskine = machines.find(x => x.id === id);
  if (!maskine) return;

  omdoeberMaskineId = id;
  const felt = document.getElementById('renameInput');
  felt.value = decodeHtml(maskine.name);
  document.getElementById('renameModal').classList.add('open');

  // Markér teksten, så man kan skrive et helt nyt navn med det samme
  // uden først at slette det gamle.
  setTimeout(() => { felt.focus(); felt.select(); }, 60);
}

export async function gemNytNavn() {
  const maskine = machines.find(x => x.id === omdoeberMaskineId);
  if (!maskine) { closeModal('renameModal'); return; }

  const nyt = document.getElementById('renameInput').value.trim();
  if (!nyt) { toast('Navnet må ikke være tomt'); return; }
  if (nyt === decodeHtml(maskine.name)) { closeModal('renameModal'); return; }

  const knap = document.getElementById('renameBtn');
  knap.disabled = true; knap.textContent = 'Gemmer…';
  try {
    const { error } = await sb.from('machines').update({ name: nyt }).eq('id', maskine.id);
    if (error) { toast('❌ Kunne ikke ændre navnet'); return; }

    maskine.name = nyt;
    const overskrift = document.getElementById('mpage-name');
    if (overskrift) overskrift.textContent = nyt;
    renderMachines();
    closeModal('renameModal');
    omdoeberMaskineId = null;
    toast('✓ Navnet er ændret');
  } finally {
    knap.disabled = false; knap.textContent = 'Gem navn';
  }
}

export function statusBadge(m) {
  // Teksten var før "+ Tilføj service", hvilket fik mærkatet til at ligne
  // en knap man kunne trykke på. Det er et statusmærkat — service
  // registreres inde på maskinen.
  if (!m._lastSvc) return '<span class="badge bb">Ingen service registreret</span>';
  const next = new Date(m._lastSvc); next.setFullYear(next.getFullYear()+1);
  const diff = Math.round((next-new Date())/(1000*60*60*24));
  if (diff < 0)  return '<span class="badge br">Service overskredet</span>';
  if (diff < 90) return '<span class="badge bo">Service snart</span>';
  return '<span class="badge bg">Service er ajour</span>';
}

export function fmtDate(d) { if (!d) return null; const dt=new Date(d); return MFULL[dt.getMonth()]+' '+dt.getFullYear(); }

/**
 * Ét maskinkort.
 *
 * Samme markup uanset opstilling — CSS afgør, om de fem dele ligger som
 * et kort (telefon) eller en række (pc). Det er derfor der ikke findes
 * to komponenter, der skal holdes ens.
 *
 * Billedet er nu et lille kvadrat. Før fyldte det halvdelen af kortet
 * uden at fortælle noget: man ved godt, hvordan ens egen maskine ser ud.
 * Pladsen bruges i stedet på næste servicedato og serviceplan.
 */
export function renderCard(m) {
  const list = document.getElementById('mcList');
  const meta = m._prodMeta || {};
  const isCustom = !m.sku;
  const imgSrc = m.custom_image_url || meta.image_url;
  const icon = isCustom ? '🔩' : '⚙️';

  const thumb = imgSrc
    ? `<img class="mc-img" src="${esc(imgSrc)}" alt="" data-mcid="${esc(m.id)}">`
    : `<span class="mc-tom">${icon}</span>`;

  const undertekst = isCustom
    ? (m.custom_serial ? `Serie: <b>${esc(m.custom_serial)}</b>` : 'Anden fabrikat')
    : `Varenr.: <b>${esc(m.sku)}</b>`;

  const svc = naesteService(m);
  const plan = m.service_plan
    ? `<span class="mc-val ok">${esc(m.service_plan)}-årsplan</span>`
    : '<span class="mc-val tom">Ingen</span>';

  const batteri = (isBatteryMachine(m) && m.battery_imei)
    ? `<div class="mc-bat-mini" id="bat-mini-${esc(m.id)}"><span style="color:var(--txt-m)">🔋 Henter…</span></div>`
    : '';

  const div = document.createElement('div');
  div.className = 'mc';
  div.id = 'mc-' + m.id;
  div.innerHTML = `
    <div class="mc-thumb">${thumb}</div>
    <div class="mc-navn">
      <div class="mcname">${visTekst(m.name)}</div>
      <div class="mcsku">${undertekst}</div>
      ${batteri}
    </div>
    <div class="mc-fakta mc-f-service">
      <span class="mc-lbl">Næste service</span>
      <span class="mc-val ${svc.klasse}">${esc(svc.tekst)}</span>
    </div>
    <div class="mc-fakta mc-f-plan">
      <span class="mc-lbl">Serviceplan</span>
      ${plan}
    </div>
    <div class="mc-goto" onclick="openMachinePage('${escJs(m.id)}')">
      <span class="mc-goto-lbl">Åbn maskinen</span>
      <span class="mc-goto-arr">›</span>
    </div>`;

  list.appendChild(div);

  const imgEl = div.querySelector('.mc-img');
  if (imgEl) imgEl.onerror = function () {
    this.outerHTML = `<span class="mc-tom">${icon}</span>`;
  };

  if (isBatteryMachine(m) && m.battery_imei) loadBatteryStatus(m.id, m.battery_imei);
}

/**
 * Næste servicedato, regnet som et år efter sidste registrerede service.
 *
 * Erstatter statusmærkatet på oversigten: en dato siger mere end
 * "service snart", og farven bærer alvoren.
 */
function naesteService(m) {
  if (!m._lastSvc) return { tekst: 'Ikke registreret', klasse: 'tom' };
  const naeste = new Date(m._lastSvc);
  naeste.setFullYear(naeste.getFullYear() + 1);
  const dage = Math.round((naeste - new Date()) / 86400000);
  const dato = naeste.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
  if (dage < 0)  return { tekst: dato, klasse: 'over' };
  if (dage < 90) return { tekst: dato, klasse: 'snart' };
  return { tekst: dato, klasse: 'ok' };
}



export function noImgHtml(icon) {
  return `<div class="mc-img-placeholder" style="height:80px"><span class="mc-img-icon">${icon}</span></div>`;
}

// ── Maskinens eget kort ─────────────────────────────────────
export async function openMachinePage(mcId) {
  const m = machines.find(x => x.id === mcId);
  if (!m) return;
  const meta = m._prodMeta || {};
  const isCustom = !m.sku;
  const imgSrc = m.custom_image_url || meta.image_url;

  document.getElementById('mpage-name').textContent = decodeHtml(m.name);
  document.getElementById('mpage-sku').textContent = m.sku ? 'Varenr.: ' + m.sku : (m.custom_serial ? 'Serie: ' + m.custom_serial : 'Anden fabrikat');

  const main = document.getElementById('mpage-main');
  const imgHtml = imgSrc
    ? `<div style="position:relative"><img src="${imgSrc}" style="width:100%;height:200px;object-fit:cover;display:block" onerror="this.style.display='none'">
        <label style="position:absolute;bottom:8px;right:8px;background:rgba(0,0,0,.45);border:1px solid rgba(255,255,255,.2);color:#fff;font-size:11px;font-family:'Oswald',sans-serif;letter-spacing:1px;text-transform:uppercase;padding:5px 10px;border-radius:4px;cursor:pointer" onclick="document.getElementById('mpage-imgup').click()">📷 Skift foto</label>
        <input type="file" id="mpage-imgup" accept="image/*" capture="environment" style="display:none" onchange="uploadMachineImage(event,'${m.id}')"></div>`
    : `<div style="background:var(--surf2);padding:20px;text-align:center">
        <div style="font-size:48px;opacity:.3">${isCustom ? '🔩' : '⚙️'}</div>
        <label style="display:inline-block;margin-top:8px;font-size:12px;color:var(--green-dk);cursor:pointer;font-family:'Oswald',sans-serif;letter-spacing:1px" onclick="document.getElementById('mpage-imgup').click()">📷 Tilføj foto</label>
        <input type="file" id="mpage-imgup" accept="image/*" capture="environment" style="display:none" onchange="uploadMachineImage(event,'${m.id}')"></div>`;

  const svcLog = m._svcLog || [];
  const lastSvc = svcLog.length ? fmtDate(svcLog[0].service_date) : null;
  const nextSvc = svcLog.length ? (() => { const d = new Date(svcLog[0].service_date); d.setFullYear(d.getFullYear()+1); return fmtDate(d.toISOString()); })() : null;
  const tlItems = svcLog.map((e,i) => {
    const d = new Date(e.service_date);
    return `<div class="tli"><div class="tldt"></div><div class="tldate">${MFULL[d.getMonth()]} ${d.getFullYear()} <span class="tlb ${i===svcLog.length-1?'tb1':'tbn'}">${i===svcLog.length-1?'1. service':'service'}</span></div><div class="tlnote">${esc(e.note||'')}</div></div>`;
  }).join('');

  // Her blev admin_note vist til kunden under overskriften "NOTE FRA
  // DK·TEC" — og uescapet. Feltet er en intern note til servicechefen;
  // se admin-panelets egen etiket og db/skema.sql.
  //
  // Bekræftet af Rikke 30-07-2026: kunden skal som udgangspunkt IKKE se
  // noten. Det er altså en truffet beslutning, ikke en antagelse — lad
  // være med at åbne den igen. Skal kunden kunne få en besked, hører det
  // til i et selvstændigt felt, adskilt fra det servicechefen skriver
  // fortroligt.
  const adminNote = '';

  const isBat = isBatteryMachine(m);
  const hasFmc = hasFMC130(m);
  const trackerType = m._tracker?.machine_type;
  const isTrackerControlled = hasFmc; // FMC130 styrer dette
  const batToggleDisabled = isTrackerControlled ? 'style="opacity:.5;pointer-events:none"' : '';
  const batToggleSub = isTrackerControlled
    ? `<span style="color:#2ecc71">● Forbundet</span>`
    : 'Slå til hvis maskinen har batteri med QR-kode';
  const batToggle = !isTrackerControlled ? `<div class="bat-toggle-row"><div><div class="bat-toggle-lbl">⚡ Batterimaskine</div><div class="bat-toggle-sub">${batToggleSub}</div></div><label class="tog" ${batToggleDisabled}><input type="checkbox" id="bat-tog-${m.id}" ${isBat ? 'checked' : ''} onchange="toggleBatteryType('${m.id}', this.checked)" ${isTrackerControlled ? 'disabled' : ''}><div class="tog-track"></div><div class="tog-thumb"></div></label></div>` : '';

  const batHtml = isBat && !hasFmc
    ? (m.battery_imei
        ? `<div class="bat-section"><div id="mpage-bat-content" class="bat-loading">Henter batteristatus…</div></div>`
        : `<div class="bat-section"><div class="bat-header"><div class="bat-title">🔋 Batteristatus</div></div><button class="bat-add-btn" onclick="openBatModal('${m.id}')">📷 Scan batteri QR-kode for at aktivere batteriovervågning</button></div>`)
    : '';

  // FMC130 tracker widget
  const trackerHtml = hasFmc ? `
    <div class="bat-section" id="fmc-section-${m.id}">
      <div class="bat-header">
        <div class="bat-title">📡 GPS & Maskindata</div>
        <div style="font-size:11px;color:var(--txt-d)" id="fmc-status-${m.id}">Henter data…</div>
      </div>
      <div id="fmc-data-${m.id}" style="display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:12px 0">
        <div style="text-align:center;padding:20px;grid-column:span 2;color:var(--txt-d);font-size:12px">Indlæser…</div>
      </div>
    </div>` : '';

  const vidBtn = !isCustom ? `<button class="mc-btn mc-btn-vid" id="mpage-vidbtn" style="display:none" onclick="showMachineVideos('${escJs(m.id)}','${escJs(m.name)}','${escJs(m.sku)}')">🎬 Vejledninger</button>` : '';
  const partsBtn = meta.url ? `<a class="bxs parts" href="${meta.url}#spare-parts-tab" target="_blank" rel="noopener">🔩 Reservedele</a>` : '';
  const batQrBtn = (isBat && !hasFmc) ? `<button class="bxs bat-qr-btn" onclick="openBatModal('${m.id}')">🔋 ${m.battery_imei ? 'Skift batteri QR' : 'Tilknyt batteri'}</button>` : '';

  main.innerHTML = `
    ${imgHtml}
    <div style="padding:14px 16px 10px">
      <div class="badges" style="margin-bottom:10px">${statusBadge(m)}${m.service_plan ? '<span class="badge bb">🛡 '+m.service_plan+'-årsplan</span>' : ''}</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <button class="mc-btn mc-btn-svc" style="flex:1" onclick="showSvcModal('${escJs(m.id)}','${escJs(m.name)}')">🔧 Registrer service</button>
        ${vidBtn}
        <a class="mc-btn mc-btn-book" style="flex:1" href="https://www.dk-tec.dk/book-service" target="_blank" rel="noopener">📅 Book service</a>
      </div>
    </div>
    <div class="mcrow"><div class="mcrl"><div class="dot dg"></div>Registreret</div><div class="mcrv">${new Date(m.added_date).toLocaleDateString('da-DK')}</div></div>
    <div class="mcrow"><div class="mcrl"><div class="dot ${lastSvc ? 'dg' : ''}"></div>Seneste service</div><div class="mcrv" style="${!lastSvc ? 'color:var(--txt-m)' : ''}">${lastSvc || 'Ikke registreret'}</div></div>
    ${nextSvc ? `<div class="mcrow"><div class="mcrl"><div class="dot do"></div>Næste service</div><div class="mcrv" style="color:#dda060">${nextSvc}</div></div>` : ''}
    <div class="tlwrap"><div class="tllbl">Servicehistorik</div><div class="tl">${tlItems || '<div style="font-size:12px;color:var(--txt-m)">Ingen service registreret endnu</div>'}</div></div>
    ${adminNote}
    ${batToggle}
    ${batHtml}
    ${trackerHtml}
    <div class="mcacts" style="background:var(--surf3);border-top:1px solid var(--bdr);padding:12px 16px;display:flex;gap:8px;flex-wrap:wrap">
      ${partsBtn}${batQrBtn}
      <button class="bxs" onclick="omdoebMaskine('${escJs(m.id)}')">✏️ Omdøb</button>
      <button class="bxs del" onclick="if(confirm('Slet maskinen?')){deleteMachine('${escJs(m.id)}','${escJs(m.name)}');showPage('dash')}">🗑 Slet maskine</button>
    </div>`;

  showPage('machine');
  if (!isCustom) {
    const { data } = await sb.from('video_machine_tags').select('video_id').eq('sku', m.sku);
    const vb = document.getElementById('mpage-vidbtn');
    if (vb && data?.length) { vb.style.display = 'inline-flex'; vb.textContent = '🎬 Vejledninger (' + data.length + ')'; }
  }
  if (isBat && m.battery_imei && !hasFmc) loadBatteryStatusFull('mpage-bat-content', m.battery_imei);
  if (hasFMC130(m)) loadFMC130Data(m.id);
}

export async function loadTimeline(mcId) {
  const mc = machines.find(m=>m.id===mcId);
  const data = mc?._svcLog;
  if (!data?.length) return;
  const lsEl=document.getElementById('ls-'+mcId);
  if (lsEl) { lsEl.textContent=fmtDate(data[0].service_date); lsEl.style.color=''; }
  const bdEl=document.getElementById('badges-'+mcId);
  if (bdEl&&mc) bdEl.innerHTML=statusBadge(mc)+(mc.service_plan?`<span class="badge bb">🛡 ${mc.service_plan}-årsplan</span>`:'');
  const next=new Date(data[0].service_date); next.setFullYear(next.getFullYear()+1);
  const nsRow=document.getElementById('ns-row-'+mcId);
  const nsEl=document.getElementById('ns-'+mcId);
  if (nsRow&&nsEl) { nsRow.style.display='flex'; nsEl.textContent=fmtDate(next.toISOString()); }
  const tlEl=document.getElementById('tl-'+mcId); if (!tlEl) return;
  const items = data.map((e,i)=>{
    const d=new Date(e.service_date);
    return `<div class="tli"><div class="tldt"></div>
      <div class="tldate">${MFULL[d.getMonth()]} ${d.getFullYear()} <span class="tlb ${i===data.length-1?'tb1':'tbn'}">${i===data.length-1?'1. service':'service'}</span></div>
      <div class="tlnote">${esc(e.note||'')}</div></div>`;
  }).join('');
  tlEl.innerHTML = items + `<div class="tli"><div class="tldt fut"></div>
    <div class="tldate" style="color:var(--txt-m)">${fmtDate(next.toISOString())} <span class="tlb tbn">Planlagt</span></div>
    <div class="tlnote" style="color:var(--txt-m)">Næste service</div></div>`;
}

export async function deleteMachine(id, name) {
  if (!confirm('Slet '+name+'? Dette kan ikke fortrydes.')) return;
  const { error } = await sb.from('machines').delete().eq('id',id);
  if (error) { toast('❌ Kunne ikke slette'); return; }
  setMachines(machines.filter(m=>m.id!==id)); renderMachines(); toast('✓ '+name+' slettet');
}
